import React, { useState } from 'react'

import { Button } from '@clientbase/clientbase-library'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useCreationBlocked } from 'hooks/useCreationBlocked'
import moment from 'moment'
import { useRouter } from 'next/router'
import { parseCookies } from 'nookies'

import { theme } from 'styles/theme'

import ActivateButton from './components/ActivateButton'
import ProgressBar from './components/Progress'
import * as S from './styles'

export type QuotaParams =
  | {
      stage: string
      free_tier: boolean
      expiresIn: string
      daysLeft?: number | null
      billing: {
        max: number
        actual: number
      }
      nfse: {
        max: number
        actual: number
      }
      contract?: {
        max: number
        actual: number
      }
    }
  | undefined

interface TopBarGlobalInfoParams {
  quota: QuotaParams
  showCoraWarning?: boolean
  quotaLoaded?: boolean
}

/**
 * Barra de conta limitada (SC-10363).
 *
 * Merchants no plano Free (descontinuado) ou com trial expirado não criam itens
 * novos. Esta barra é o lembrete permanente — aparece em todas as telas, tem
 * precedência sobre o aviso de quota do trial e não é dispensável. O CTA abre o
 * ModalPricing, o mesmo que os botões de criar disparam.
 *
 * A faixa escura à esquerda funciona como etiqueta de estado: o olho a lê antes
 * do texto, o que deixa a frase livre para explicar em vez de rotular.
 */
const AccountLimitedBar = () => {
  const { openUpgradeModal } = useCreationBlocked()
  const isCompact = useMediaQuery('(max-width: 900px)', { noSsr: false })

  return (
    <S.TopBarGlobalInfo>
      <Stack
        direction="row"
        alignItems="stretch"
        width="100%"
        gap="16px"
        sx={{
          [theme.breakpoints.down(450)]: {
            flexWrap: 'wrap',
            alignItems: 'center',
            px: '12px',
            py: '4px',
            gap: '8px'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            px: '16px',
            backgroundColor: 'rgba(0,0,0,0.22)',
            fontSize: '11.5px',
            fontWeight: 700,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            [theme.breakpoints.down(450)]: {
              px: '10px',
              py: '3px',
              borderRadius: '4px'
            }
          }}
        >
          Conta limitada
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flex={1}
          gap="16px"
          pr={{ xs: '12px', sm: '20px', md: '35px' }}
          minWidth={0}
          sx={{
            [theme.breakpoints.down(450)]: {
              pr: 0,
              width: '100%'
            }
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '12px', md: '14px' },
              fontWeight: 400,
              color: '#fff',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {isCompact
              ? 'Sua conta está limitada.'
              : 'Assine um plano para criar novos clientes, cobranças, contratos, notas e despesas. Seus dados, saldo e relatórios continuam disponíveis normalmente.'}
          </Typography>

          <Button
            variantButton="primary"
            onClick={() => openUpgradeModal('Criação de novos itens')}
            style={{
              backgroundColor: '#fff',
              color: '#294266',
              fontWeight: 600,
              padding: '8px 24px',
              minWidth: 'fit-content'
            }}
          >
            Escolher plano
          </Button>
        </Stack>
      </Stack>
    </S.TopBarGlobalInfo>
  )
}

const QuotaDialogItem = ({
  label,
  value,
  description
}: {
  label: string
  value: { actual: number; max: number }
  description: string
}) => {
  const percentage = (value.actual / value.max) * 100
  return (
    <Stack gap="6px">
      <Stack direction="row" justifyContent="space-between">
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          {value.actual} de {value.max}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 6,
          borderRadius: 4,
          backgroundColor: theme.palette.neutral[700],
          '& .MuiLinearProgress-bar': {
            backgroundColor: theme.palette.primary.main
          }
        }}
      />
      <Typography
        sx={{
          fontSize: '12px',
          color: theme.palette.neutral[400]
        }}
      >
        {description}
      </Typography>
    </Stack>
  )
}

/**
 * Copy do contador de dias de trial (SC-10668).
 *
 * O prazo de 30 dias sempre existiu no backend (MerchantCapability::
 * TRIAL_DURATION) e já bloqueava a criação ao vencer, mas no produto ele só
 * aparecia no tooltip do chip — ninguém passa o mouse num chip para descobrir
 * que a conta tem prazo. Aqui ele vira texto visível.
 *
 * Retorna null quando não há prazo a mostrar (merchant fora do trial), para o
 * chip cair no rótulo simples de antes.
 */
const trialCountdownLabel = (daysLeft?: number | null) => {
  if (daysLeft === null || daysLeft === undefined) return null
  if (daysLeft <= 0) return 'termina hoje'
  if (daysLeft === 1) return '1 dia restante'
  return `${daysLeft} dias restantes`
}

// Últimos 7 dias acendem o contador em âmbar. É o intervalo em que o aviso
// deixa de ser informativo e passa a exigir decisão de quem lê.
const TRIAL_WARNING_THRESHOLD_DAYS = 7

// Âmbar fixo em vez de theme.palette.warning[N]: os índices numéricos da
// warning não são garantidos neste tema (o resto do codebase sempre usa
// fallback hex ao lê-los) e um undefined aqui apagaria o fundo do chip.
const TRIAL_WARNING_BG = '#FFD27A'

const TopBarGlobalInfo = ({
  quota,
  showCoraWarning = false,
  quotaLoaded = false
}: TopBarGlobalInfoParams) => {
  const {
    ['nextauth.status']: status,
    ['nextauth.current_plan']: currentPlan
  } = parseCookies()
  const isMobile = useMediaQuery('(max-width: 770px)', { noSsr: false })
  const isCompact = useMediaQuery('(max-width: 850px)', { noSsr: false })
  const isTrial = currentPlan === 'plan_trial'
  const router = useRouter()
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false)
  const { creationBlocked } = useCreationBlocked()

  /**
   * Ativação pendente em plano pago (SC-10879).
   *
   * `merchants.status` continua `trial` até a ativação sair, mesmo depois de o
   * merchant assinar. Nesse recorte a barra existe só para carregar o CTA de
   * concluir cadastro: o chip TRIAL mentiria (ele paga), o tooltip de período
   * gratuito não se aplica e o contador de dias conta um prazo que não é o
   * dele. Quem está de fato em trial (`isTrial`) segue vendo tudo como antes.
   */
  const isPaidPendingActivation = status === 'trial' && !isTrial

  const countdownLabel = isPaidPendingActivation
    ? null
    : trialCountdownLabel(quota?.daysLeft)
  const isTrialEnding =
    !isPaidPendingActivation &&
    quota?.daysLeft !== null &&
    quota?.daysLeft !== undefined &&
    quota.daysLeft <= TRIAL_WARNING_THRESHOLD_DAYS

  const handleConfigureCora = () => {
    router.push('/settings?tab=banco-cora')
  }

  // Conta limitada tem precedência: quem não pode criar não precisa do aviso de
  // quota do trial, que ficaria contraditório ao lado dele (SC-10363).
  if (creationBlocked) return <AccountLimitedBar />

  return (
    <>
      <S.TopBarGlobalInfo>
        {/* rounded corners - hidden on small screens */}
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Box
            sx={{
              backgroundColor: '#294266',
              width: '20px',
              height: '20px',
              bottom: '-20px',
              left: 0,
              position: 'absolute'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              width: '20px',
              height: '20px',
              backgroundColor: '#f2f5f2',
              clipPath: 'circle(90.4% at 90% 90%)',
              bottom: '-20px'
            }}
          />
          <Box
            sx={{
              backgroundColor: '#294266',
              width: '20px',
              height: '20px',
              right: 0,
              bottom: '-20px',
              position: 'absolute'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              width: '20px',
              height: '20px',
              backgroundColor: isMobile ? '#f2f5f2' : '#fcfcfc',
              clipPath: 'circle(90.4% at 10% 90%)',
              bottom: '-20px'
            }}
          />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          px={{ xs: '12px', sm: '20px', md: '35px' }}
          gap="8px"
          sx={{
            [theme.breakpoints.down(450)]: {
              flexWrap: 'wrap'
            }
          }}
        >
          {/* Left: TRIAL tag */}
          <Box
            sx={{
              flexShrink: 0,
              [theme.breakpoints.down(450)]: {
                flex: 1
              }
            }}
          >
            {/* O contador de dias chega junto com a quota, uma request depois
                da barra aparecer. Sem o skeleton o chip renderiza "TRIAL" e
                troca para "TRIAL · N dias restantes" quando a resposta volta —
                um pisca-pisca com mudança de largura. Segura o mesmo tamanho do
                chip final e usa o skeleton das barras de progresso. */}
            {/* Merchant pago com ativação pendente não espera contador: o
                skeleton existe só para segurar a largura de "TRIAL · N dias",
                que aqui nunca chega. Renderiza o rótulo direto. */}
            {!showCoraWarning && isPaidPendingActivation && (
              <Typography
                sx={{ fontSize: { xs: '11px', md: '14px' } }}
                color="#fff"
                variant="smLight"
              >
                Conclua o cadastro para ativar sua conta
              </Typography>
            )}
            {!showCoraWarning && !isPaidPendingActivation && !quotaLoaded && (
              <Skeleton
                variant="rounded"
                width={186}
                height={26}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: '999px'
                }}
              />
            )}
            {!showCoraWarning && !isPaidPendingActivation && quotaLoaded && (
              <Tooltip
                title={`Seu período gratuito está ativo até ${moment(
                  quota?.expiresIn
                ).format('DD/MM/YYYY')}`}
                arrow
                placement="bottom"
              >
                <Box
                  sx={{
                    backgroundColor: isTrialEnding ? TRIAL_WARNING_BG : '#fff',
                    color: '#294266',
                    fontWeight: 700,
                    fontSize: '12px',
                    padding: '4px 12px',
                    borderRadius: '999px',
                    cursor: 'default',
                    letterSpacing: '0.5px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    // Mesma largura do skeleton: sem isso o chip nasce curto
                    // ("TRIAL") e alarga ao receber o contador, empurrando o
                    // resto da barra.
                    minWidth: countdownLabel ? '186px' : undefined
                  }}
                >
                  {countdownLabel ? `TRIAL · ${countdownLabel}` : 'TRIAL'}
                </Box>
              </Tooltip>
            )}
            {showCoraWarning && (
              <Typography
                sx={{ fontSize: { xs: '11px', md: '14px' } }}
                color="#fff"
                variant="smLight"
              >
                Configure sua conta Cora para aproveitar todos os benefícios do
                seu plano
              </Typography>
            )}
          </Box>

          {/* Center: Progress bars (desktop) or button (compact) */}
          {(quota?.free_tier || isTrial) && !showCoraWarning && (
            <>
              {isCompact ? (
                <Box
                  onClick={() => setQuotaDialogOpen(true)}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '12px',
                    padding: '6px 16px',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    marginRight: 'auto',
                    textAlign: 'center',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.25)'
                    },
                    [theme.breakpoints.down(450)]: {
                      flex: 1
                    }
                  }}
                >
                  Ver meu consumo
                </Box>
              ) : (
                <Stack
                  direction="row"
                  gap="24px"
                  justifyContent="center"
                  sx={{ flex: 1 }}
                >
                  {!quotaLoaded ? (
                    <>
                      <Skeleton
                        variant="rounded"
                        width={145}
                        height={28}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.15)',
                          borderRadius: '4px'
                        }}
                      />
                      <Skeleton
                        variant="rounded"
                        width={145}
                        height={28}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.15)',
                          borderRadius: '4px'
                        }}
                      />
                      <Skeleton
                        variant="rounded"
                        width={145}
                        height={28}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.15)',
                          borderRadius: '4px'
                        }}
                      />
                    </>
                  ) : (
                    <>
                      {quota?.billing && (
                        <ProgressBar
                          label="Cobranças"
                          value={quota?.billing}
                          tooltip={`Você pode emitir até ${quota.billing.max} cobranças via PIX/boleto sem custo durante o período gratuito`}
                        />
                      )}
                      {quota?.contract && (
                        <ProgressBar
                          label="Contratos"
                          value={quota?.contract}
                          tooltip={`Você pode enviar até ${quota.contract.max} contratos para assinatura digital sem custo durante o período gratuito`}
                        />
                      )}
                      {quota?.nfse && (
                        <ProgressBar
                          label="Notas Fiscais"
                          value={quota?.nfse}
                          tooltip={`Você pode emitir até ${quota.nfse.max} notas fiscais sem custo durante o período gratuito`}
                        />
                      )}
                    </>
                  )}
                </Stack>
              )}
            </>
          )}

          {/* Right: Action button */}
          <Box
            sx={{
              flexShrink: 0,
              [theme.breakpoints.down(450)]: {
                width: '100%'
              }
            }}
          >
            {/* O CTA não espera a quota: em ativação pendente ele é a razão de
                a barra existir, e `get_status_activation` falha em silêncio —
                amarrá-lo a `quotaLoaded` deixaria o merchant sem caminho de
                ativação sempre que a request caísse (SC-10879). No trial o
                gate segue valendo, para o botão não piscar antes do chip. */}
            {/* SC-11480: quem decide o CTA é `merchants.status`, não o plano.
                Conta ativada que segue no `plan_trial` (período gratuito ainda
                correndo) continua vendo o chip e a quota, mas não tem cadastro
                a concluir: o botão levava a /ativar-conta, que não tinha o que
                mostrar e travava no loader. */}
            {(isPaidPendingActivation || quotaLoaded) &&
              status === 'trial' &&
              !showCoraWarning && <ActivateButton />}
            {showCoraWarning && (
              <Button
                variantButton="primary"
                onClick={handleConfigureCora}
                style={{
                  backgroundColor: '#fff',
                  color: '#294266',
                  fontWeight: 600,
                  padding: '8px 24px',
                  minWidth: 'fit-content'
                }}
              >
                Configurar Agora
              </Button>
            )}
          </Box>
        </Stack>
      </S.TopBarGlobalInfo>

      {/* Quota Dialog for compact screens */}
      <Dialog
        open={quotaDialogOpen}
        onClose={() => setQuotaDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '18px' }}>
            Consumo do Trial
          </Typography>
          <IconButton onClick={() => setQuotaDialogOpen(false)} size="small">
            ✕
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          {/* Prazo antes das quotas: é o dado que gera decisão, e até o
              SC-10668 ele só existia no fim do texto (ou num tooltip). */}
          <Typography
            sx={{
              fontSize: '13px',
              color: theme.palette.neutral[400],
              mb: 3
            }}
          >
            {countdownLabel ? (
              <>
                Seu período gratuito{' '}
                {countdownLabel === 'termina hoje' ? '' : 'tem '}
                <b>{countdownLabel}</b> — até{' '}
                {moment(quota?.expiresIn).format('DD/MM/YYYY')}
              </>
            ) : (
              <>
                Seu período gratuito está ativo até{' '}
                <b>{moment(quota?.expiresIn).format('DD/MM/YYYY')}</b>
              </>
            )}
          </Typography>

          <Stack gap="20px">
            {quota?.billing && (
              <QuotaDialogItem
                label="Cobranças"
                value={quota.billing}
                description={`Você pode emitir até ${quota.billing.max} cobranças via PIX/boleto sem custo`}
              />
            )}
            {quota?.contract && (
              <QuotaDialogItem
                label="Contratos"
                value={quota.contract}
                description={`Você pode enviar até ${quota.contract.max} contratos para assinatura digital sem custo`}
              />
            )}
            {quota?.nfse && (
              <QuotaDialogItem
                label="Notas Fiscais"
                value={quota.nfse}
                description={`Você pode emitir até ${quota.nfse.max} notas fiscais sem custo`}
              />
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TopBarGlobalInfo
